import moment from "moment";
import { ButtonsBloc, FGListen, FGTextAreaInput, FGTextInput, FieldGroup, InlineButtonContainer } from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import * as Yup from "yup";

import {
  DogApi,
  ERequestState,
  PersonApi,
  RequestApi,
  RequestDto,
  WorkAttestationRequestApi,
  WorkAttestationRequestDto,
} from "../../../../api";
import {
  CancelButton,
  FGCountrySelect,
  FGDogSearchInput,
  FGPeopleSearchInput,
  FGSRSHDateMaskInput,
  InformationTooltip,
  SharepointButton,
  SmallFormGenerator,
} from "../../../../components";
import { useDialog, useEventsContext } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useCrudApi, useTl, useWindow } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { RequestDogOwnerList } from "../../RequestDogOwnerList";

export interface IWorkAttestationRequestDetailPageProps {
  workAttestationRequestId: number;
  editMode: boolean;
}

export const WorkAttestationRequestDetailPage: React.FunctionComponent<IWorkAttestationRequestDetailPageProps> = ({
  workAttestationRequestId,
  editMode,
}) => {
  const { t } = useTl();
  const history = useHistory();
  const api = useApiService(WorkAttestationRequestApi);
  const dogApi = useApiService(DogApi);
  const personApi = useApiService(PersonApi);
  const { showDialogPromise } = useDialog();
  const { openNewTab } = useWindow();

  const [dogIdValue, setDogIdValue] = React.useState(0);
  const [dogIdRequest, setDogIdRequest] = React.useState(true);
  const [dogBirthYear, setDogBirthYear] = React.useState(new Date().getFullYear());

  const { data, loading, saving, saveItem, refresh } = useCrudApi<WorkAttestationRequestDto>(
    React.useMemo(
      () => ({
        getApiFn: (id = workAttestationRequestId) =>
          id > 0
            ? api.getWorkAttestationRequest(id)
            : new WorkAttestationRequestDto({
                request: new RequestDto({
                  requestDate: new Date(),
                  status: ERequestState.REQUESTED,
                }),
              }).toJSON(),
        saveApiFn: (d: WorkAttestationRequestDto) => api.saveWorkAttestationRequest(d),
        onSavedRoute: (d) => `${ERoutes.workAttestationRequest}/${d.workAttestationRequestId}/detail/edit`,
      }),
      [api, workAttestationRequestId]
    ),
    [workAttestationRequestId]
  );

  const [saveChecking, setSaveChecking] = React.useState(false);
  const saveCheck = React.useCallback(
    async (values) => {
      setSaveChecking(true);
      let isBanned = false;
      if (workAttestationRequestId <= 0) {
        isBanned = (await personApi.isPersonExcludedQuery(values?.request?.personId)).value;
      }
      if (isBanned) {
        const isbannedValidation = await showDialogPromise({
          message: t(ETLCodes.PersonIsBannedConfirmMessage),
          title: t(ETLCodes.Warning),
        });
        if (isbannedValidation !== "yes") return;
      }
      setSaveChecking(false);
      await saveItem(values);
    },
    [workAttestationRequestId, personApi, saveItem, showDialogPromise, t]
  );

  const onRequestRefreshEvent = React.useCallback(() => {
    refresh();
  }, [refresh]);

  const { subscribeToEvent, unsubscribeEvent } = useEventsContext();
  React.useEffect(() => {
    subscribeToEvent("RequestRefresh", onRequestRefreshEvent);
    return () => unsubscribeEvent("RequestRefresh", onRequestRefreshEvent);
  }, [onRequestRefreshEvent, subscribeToEvent, unsubscribeEvent]);

  React.useEffect(() => {
    if (data && dogIdRequest) {
      setDogIdValue(data.dogId);
      setDogIdRequest(false);
    }
  }, [data, dogIdRequest]);

  const RequestedFormSchema = React.useMemo(() => {
    return Yup.object().shape({
      dogId: Yup.number()
        .nullable()
        .required(t(ETLCodes.Required))
        .test("dog-availability", t(ETLCodes.DogHasAnotherRequestError), async function (value) {
          const res = await api.isDogAvailable(value, workAttestationRequestId);
          return res.value;
        })
        .test("dog-deceased", t(ETLCodes.DogIsDeceased), async function (value) {
          if (workAttestationRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
            const res = await dogApi.isDogDeceased(value);
            return !res.value;
          }
          return true;
        })
        .test("is-not-excluded", t(ETLCodes.DogIsExcluded), async function (value) {
          if (workAttestationRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
            const result = await dogApi.isDogExcluded(value);
            return !result.value;
          }
          return true;
        }),
      competitionCountryId: Yup.number().nullable().required(t(ETLCodes.Required)),
      competitionDate: Yup.string()
        .nullable()
        .required(t(ETLCodes.Required))
        .test(
          "date-test",
          t(ETLCodes.DateMustBeGreaterThanBirthDate, {
            birthdate: dogBirthYear,
          }),
          async function (value) {
            const currentDog = await dogApi.getDog(dogIdValue);
            setDogBirthYear(currentDog?.birthDate.getFullYear());
            if (this.parent.dogId && currentDog) {
              return moment(value).isAfter(currentDog.birthDate);
            }
          }
        ),
      competitionCity: Yup.string().nullable().required(t(ETLCodes.Required)),
      competitionType: Yup.string().nullable().required(t(ETLCodes.Required)),
      competitionJudge: Yup.string().nullable().required(t(ETLCodes.Required)),
      competitionPoints: Yup.string().required(t(ETLCodes.Required)),
      request: Yup.object().shape({
        personId: Yup.number()
          .nullable()
          .required(t(ETLCodes.Required))
          .test("is-not-deseased", t(ETLCodes.PersonISDeceased), async function (value) {
            if (workAttestationRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              const result = await personApi.isDeceased(value);
              return !result.value;
            }
            return true;
          })
          .test("missing-adress-information", t(ETLCodes.MissingCompleteAdressOrEmail), async function (value) {
            if (workAttestationRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              if (value !== 0 && value !== null && value !== undefined) {
                const result = await personApi.missingAddressInformationCheck(value);
                return result.value;
              }
              return true;
            }
            return true;
          })
          .test("temporary-owner", t(ETLCodes.OwnerIsTemporaryError), async function (value) {
            const res = await dogApi.isDogTemporaryOwner(dogIdValue, value);
            return !res.value;
          })
          .test("is-owner", t(ETLCodes.IsNotDogOwner), async function (value) {
            const res = await dogApi.isOwner(dogIdValue, value);
            return res.value;
          }),
        requestDate: Yup.date().nullable().required(t(ETLCodes.Required)),
      }),
    });
  }, [api, data, dogApi, dogBirthYear, dogIdValue, personApi, t, workAttestationRequestId]);

  const OtherFormSchema = React.useMemo(() => {
    return Yup.object().shape({});
  }, []);

  const statusEditable = React.useMemo(() => {
    if (data) {
      if (data.request.status) {
        return data.request.status === ERequestState.REQUESTED;
      } else {
        return true;
      }
    }
    return false;
  }, [data]);

  const requestApi = useApiService(RequestApi);
  const [shpLoading, setShpLoading] = React.useState(false);
  const existsInShp = React.useMemo(() => !!data?.request.sharepointUri, [data?.request]);
  const openOrCreateInShp = React.useCallback(async () => {
    setShpLoading(true);
    if (!!data?.request.sharepointUri) {
      window.open(data?.request.sharepointUri, "_blank");
      setShpLoading(false);
    } else {
      try {
        await requestApi.createInSharepoint(data?.requestId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setShpLoading(false);
      }
    }
  }, [data?.request.sharepointUri, data?.requestId, requestApi, refresh]);

  const [dogShpLoading, setClubShpLoading] = React.useState(false);
  const dogExistsInShp = React.useMemo(() => !!data?.dogSharepointUri, [data?.dogSharepointUri]);
  const openOrCreateDogInShp = React.useCallback(async () => {
    setClubShpLoading(true);
    if (!!data?.dogSharepointUri) {
      window.open(data?.dogSharepointUri, "_blank");
      setClubShpLoading(false);
    } else {
      try {
        await dogApi.createInSharepoint(data?.dogId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setClubShpLoading(false);
      }
    }
  }, [data?.dogSharepointUri, data?.dogId, dogApi, refresh]);

  return (
    <>
      <SmallFormGenerator
        initialValues={data}
        editMode={editMode}
        onSubmit={saveCheck}
        validationSchema={data?.request?.status === ERequestState.REQUESTED ? RequestedFormSchema : OtherFormSchema}
        loading={loading}
        onCancel={() => history.push(ERoutes.workAttestationRequest)}
        saving={saving || saveChecking}
        hideButtons={data?.request?.status === ERequestState.EXECUTED}
      >
        <FGListen
          field="dogId"
          onChanged={(value) => {
            setDogIdValue(value);
          }}
        />
        <FieldGroup
          columns={2}
          fieldsetProps={{
            title: t(ETLCodes.RequestData),
            rightElement: workAttestationRequestId > 0 && (
              <SharepointButton existsInShp={existsInShp} loading={shpLoading} onClick={openOrCreateInShp} />
            ),
          }}
        >
          <div>
            <FGSRSHDateMaskInput
              name="request.requestDate"
              label={t(ETLCodes.RequestDate)}
              requiredMark
              readonly={!statusEditable || data?.request?.isBlocked}
            />
            {data?.request?.status === ERequestState.EXECUTED && (
              <FieldGroup columns={2}>
                <FGSRSHDateMaskInput name="request.executionDate" label={t(ETLCodes.ClosingDate)} readonly={true} />
              </FieldGroup>
            )}
            <FGDogSearchInput
              name="dogId"
              label={t(ETLCodes.Dog)}
              onItemViewClick={(item) => openNewTab(`${ERoutes.dog}/${item}/detail/view`)}
              readonly={!statusEditable || data?.request?.isBlocked}
              info={
                workAttestationRequestId > 0 && (
                  <SharepointButton
                    existsInShp={dogExistsInShp}
                    loading={dogShpLoading}
                    onClick={openOrCreateDogInShp}
                  />
                )
              }
            />
            <FGPeopleSearchInput
              name="request.personId"
              label={t(ETLCodes.Contact)}
              onItemViewClick={(item) => openNewTab(`${ERoutes.people}/${item}/detail/view`)}
              requiredMark
              readonly={!statusEditable || data?.request?.isBlocked}
              info={<InformationTooltip tooltipMessage={ETLCodes.Anyone} />}
              dogId={dogIdValue}
              showWarning={true}
            />
          </div>

          <FGTextAreaInput
            name="request.remark"
            label={t(ETLCodes.Remark)}
            readonly={data?.request?.status === ERequestState.EXECUTED}
            rows={5}
          />
        </FieldGroup>

        {data?.request?.status === ERequestState.EXECUTED && (
          <InlineButtonContainer>
            <div />
            <ButtonsBloc style={{ float: "right" }}>
              <CancelButton minimal={false} onClick={() => history.push(`${ERoutes.workAttestationRequest}`)} />
            </ButtonsBloc>
          </InlineButtonContainer>
        )}
        <FieldGroup columns={2} fieldsetProps={{ title: t(ETLCodes.Competition) }}>
          <FGCountrySelect
            name="competitionCountryId"
            label={t(ETLCodes.Country)}
            readonly={!statusEditable || data?.request?.isBlocked}
          />
          <FGSRSHDateMaskInput
            label={t(ETLCodes.Date)}
            name="competitionDate"
            readonly={!statusEditable || data?.request?.isBlocked}
          />
          <FGTextInput
            label={t(ETLCodes.Points)}
            name="competitionPoints"
            readonly={!statusEditable || data?.request?.isBlocked}
          />
          <FGTextInput
            name="competitionCity"
            label={t(ETLCodes.City)}
            readonly={!statusEditable || data?.request?.isBlocked}
          />
          <FGTextInput
            name="competitionType"
            label={t(ETLCodes.Type)}
            readonly={!statusEditable || data?.request?.isBlocked}
          />
          <FGTextInput
            name="competitionJudge"
            label={t(ETLCodes.Judge)}
            readonly={!statusEditable || data?.request?.isBlocked}
          />
        </FieldGroup>
        <FieldGroup columns={1}>
          <RequestDogOwnerList dogId={dogIdValue} />
        </FieldGroup>
      </SmallFormGenerator>
    </>
  );
};
